import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, Plus, Phone, Mail, Clock, AlertTriangle, Check, 
  X, User, Calendar, Filter, ChevronRight, Eye, FileText
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { PatientSearchSelect } from '../components/PatientSearchSelect';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Interest level colors
const INTEREST_COLORS = {
  alto: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  medio: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  bajo: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300' }
};

// Status labels
const STATUS_LABELS = {
  activo: { label: 'Activo', color: 'bg-blue-500' },
  operado: { label: 'Operado', color: 'bg-green-500' },
  desistio: { label: 'Desistió', color: 'bg-red-500' },
  en_pausa: { label: 'En pausa', color: 'bg-slate-400' }
};

// Contact results
const CONTACT_RESULTS = [
  { value: 'contesto', label: 'Contestó' },
  { value: 'no_contesto', label: 'No contestó' },
  { value: 'buzon', label: 'Buzón' },
  { value: 'agendo_consulta', label: 'Agendó consulta' },
  { value: 'agendo_cirugia', label: 'Agendó cirugía' },
  { value: 'pidio_tiempo', label: 'Pidió tiempo' },
  { value: 'desistio', label: 'Desistió' }
];

const CONTACT_METHODS = [
  { value: 'llamada', label: 'Llamada' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'correo', label: 'Correo' },
  { value: 'presencial', label: 'Presencial' }
];

const SurgeryCRMPage = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState({ total_activos: 0, a_contactar_hoy: 0, vencidos: 0, operados_este_mes: 0 });
  const [interested, setInterested] = useState([]);
  const [surgeryTypes, setSurgeryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEstado, setFilterEstado] = useState('activo');
  const [filterNivel, setFilterNivel] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedInterested, setSelectedInterested] = useState(null);
  const [showTypesModal, setShowTypesModal] = useState(false);
  const [isNewPatient, setIsNewPatient] = useState(false);

  // New interested form
  const [newInterested, setNewInterested] = useState({
    paciente_id: '',
    nombre_completo: '',
    telefono: '',
    correo: '',
    tipo_cirugia_id: '',
    nivel_interes: 'medio',
    observaciones_generales: ''
  });

  // New patient form (for creating patient inline)
  const [newPatient, setNewPatient] = useState({
    full_name: '',
    phone: '',
    email: ''
  });

  // New contact form
  const [newContact, setNewContact] = useState({
    medio: 'llamada',
    resultado: '',
    notas: '',
    nivel_interes_actualizado: ''
  });

  // New surgery type form
  const [newSurgeryType, setNewSurgeryType] = useState({ name: '', description: '' });

  // Fetch data
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surgery-crm/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInterested, setTotalInterested] = useState(0);
  const PAGE_SIZE = 50;

  const fetchInterested = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (filterEstado && filterEstado !== 'todos') params.estado = filterEstado;
      if (filterNivel && filterNivel !== 'todos') params.nivel_interes = filterNivel;
      if (filterTipo && filterTipo !== 'todos') params.tipo_cirugia_id = filterTipo;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/surgery-crm/interested/paginated`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInterested(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotalInterested(response.data.total);
    } catch (error) {
      console.error('Error fetching interested:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, filterEstado, filterNivel, filterTipo, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [filterEstado, filterNivel, filterTipo, searchQuery]);

  const fetchSurgeryTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/surgery-crm/surgery-types`);
      setSurgeryTypes(response.data);
    } catch (error) {
      console.error('Error fetching surgery types:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSurgeryTypes();
  }, [fetchStats, fetchSurgeryTypes]);

  useEffect(() => {
    fetchInterested();
  }, [fetchInterested]);

  // Handle patient selection
  const handlePatientSelect = (patient) => {
    setNewInterested(prev => ({
      ...prev,
      paciente_id: patient.id,
      nombre_completo: patient.full_name,
      telefono: patient.phone || '',
      correo: patient.email || ''
    }));
  };

  // Create new interested
  const handleCreateInterested = async () => {
    // If creating new patient first
    if (isNewPatient) {
      if (!newPatient.full_name || !newPatient.phone) {
        toast.error('Nombre y teléfono son requeridos');
        return;
      }
      if (!newInterested.tipo_cirugia_id) {
        toast.error('Selecciona un tipo de cirugía');
        return;
      }
      
      try {
        // Create patient first
        const patientResponse = await axios.post(`${API}/patients`, newPatient, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const createdPatient = patientResponse.data;
        
        // Then create interested with the new patient
        await axios.post(`${API}/surgery-crm/interested`, {
          ...newInterested,
          paciente_id: createdPatient.id,
          nombre_completo: createdPatient.full_name,
          telefono: createdPatient.phone,
          correo: createdPatient.email || ''
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Paciente e interesado registrados');
        setShowNewModal(false);
        setIsNewPatient(false);
        setNewPatient({ full_name: '', phone: '', email: '' });
        setNewInterested({
          paciente_id: '', nombre_completo: '', telefono: '', correo: '',
          tipo_cirugia_id: '', nivel_interes: 'medio', observaciones_generales: ''
        });
        fetchInterested();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al registrar');
      }
    } else {
      // Existing flow - select existing patient
      if (!newInterested.paciente_id || !newInterested.tipo_cirugia_id) {
        toast.error('Selecciona un paciente y tipo de cirugía');
        return;
      }
      try {
        await axios.post(`${API}/surgery-crm/interested`, newInterested, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Paciente interesado registrado');
        setShowNewModal(false);
        setNewInterested({
          paciente_id: '', nombre_completo: '', telefono: '', correo: '',
          tipo_cirugia_id: '', nivel_interes: 'medio', observaciones_generales: ''
        });
        fetchInterested();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al registrar');
      }
    }
  };

  // Create contact record
  const handleCreateContact = async () => {
    if (!newContact.resultado || !newContact.notas || newContact.notas.length < 5) {
      toast.error('Selecciona un resultado y escribe notas (mínimo 5 caracteres)');
      return;
    }
    try {
      await axios.post(
        `${API}/surgery-crm/interested/${selectedInterested.id}/contacts`,
        newContact,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Contacto registrado');
      setShowContactModal(false);
      setNewContact({ medio: 'llamada', resultado: '', notas: '', nivel_interes_actualizado: '' });
      fetchInterested();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar contacto');
    }
  };

  // Update status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await axios.put(
        `${API}/surgery-crm/interested/${id}`,
        { estado: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Estado actualizado');
      fetchInterested();
      fetchStats();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Create surgery type
  const handleCreateSurgeryType = async () => {
    if (!newSurgeryType.name) {
      toast.error('Ingresa el nombre del tipo de cirugía');
      return;
    }
    try {
      await axios.post(`${API}/surgery-crm/surgery-types`, newSurgeryType, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipo de cirugía creado');
      setNewSurgeryType({ name: '', description: '' });
      fetchSurgeryTypes();
    } catch (error) {
      toast.error('Error al crear tipo de cirugía');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-slate-900">CRM Cirugías</h1>
          <p className="text-slate-500 text-sm">Seguimiento de pacientes interesados en cirugía</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTypesModal(true)}>
            Tipos de cirugía
          </Button>
          <Button onClick={() => setShowNewModal(true)} className="bg-[#c2410c] hover:bg-[#a13308] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo interesado
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-3xl font-display font-medium text-slate-900">{stats.total_activos}</div>
            <div className="text-sm text-slate-500">Total activos</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-display font-medium text-amber-700">{stats.a_contactar_hoy}</div>
            <div className="text-sm text-amber-600">A contactar hoy</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-display font-medium text-red-700">{stats.vencidos}</div>
            <div className="text-sm text-red-600">Vencidos (+15 días)</div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-3xl font-display font-medium text-green-700">{stats.operados_este_mes}</div>
            <div className="text-sm text-green-600">Operados este mes</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre o teléfono..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterEstado || "activo"} onValueChange={setFilterEstado}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="activo">Activos</SelectItem>
            <SelectItem value="operado">Operados</SelectItem>
            <SelectItem value="desistio">Desistieron</SelectItem>
            <SelectItem value="en_pausa">En pausa</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterNivel || "todos"} onValueChange={setFilterNivel}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Nivel interés" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alto">Alto</SelectItem>
            <SelectItem value="medio">Medio</SelectItem>
            <SelectItem value="bajo">Bajo</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterTipo || "todos"} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Tipo cirugía" />
          </SelectTrigger>
          <SelectContent>
            {surgeryTypes.map(type => (
              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
            ))}
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Paciente</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Cirugía</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Nivel</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Último contacto</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Próximo</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Intentos</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Observaciones</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-500">Cargando...</td>
                </tr>
              ) : interested.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-500">No hay pacientes interesados</td>
                </tr>
              ) : (
                interested.map((item) => {
                  const interestStyle = INTEREST_COLORS[item.nivel_interes] || INTEREST_COLORS.medio;
                  const isOverdue = item.esta_vencido;
                  const hasMultipleAttempts = item.contador_contactos_sin_respuesta >= 3;

                  return (
                    <tr key={item.id} className={`border-b hover:bg-slate-50 ${isOverdue ? 'bg-red-50' : ''}`}>
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{item.nombre_completo}</div>
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          {item.telefono}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-700">{item.tipo_cirugia_nombre}</span>
                      </td>
                      <td className="p-4">
                        <Badge className={`${interestStyle.bg} ${interestStyle.text} border ${interestStyle.border}`}>
                          {item.nivel_interes.charAt(0).toUpperCase() + item.nivel_interes.slice(1)}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {item.dias_desde_ultimo_contacto !== null ? (
                          <span className={item.dias_desde_ultimo_contacto > 15 ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            hace {item.dias_desde_ultimo_contacto} días
                          </span>
                        ) : (
                          <span className="text-slate-400">Sin contacto</span>
                        )}
                      </td>
                      <td className="p-4">
                        {item.fecha_proximo_contacto ? (
                          <Badge className={isOverdue ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>
                            {isOverdue && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {format(parseISO(item.fecha_proximo_contacto), 'dd/MM/yyyy')}
                          </Badge>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        {hasMultipleAttempts ? (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {item.contador_contactos_sin_respuesta}+ sin respuesta
                          </Badge>
                        ) : (
                          <span className="text-slate-500">{item.contador_contactos_sin_respuesta}</span>
                        )}
                      </td>
                      <td className="p-4 max-w-xs">
                        <p className="text-sm text-slate-600 line-clamp-2" title={item.observaciones_generales || ''} data-testid={`crm-observaciones-${item.id}`}>
                          {item.observaciones_generales || <span className="text-slate-400">—</span>}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedInterested(item); setShowContactModal(true); }}
                            title="Registrar contacto"
                          >
                            <Phone className="w-4 h-4" />
                          </Button>
                          {item.evaluacion_id && (
                            <Link to={`/admin/patients/${item.paciente_id}/evaluacion-kanban?evaluacion=${item.evaluacion_id}`}>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-[#0B3B71] gap-1"
                                title={`Ver evaluación donde se recomendó la cirugía (${format(parseISO(item.evaluacion_fecha), 'dd/MM/yyyy')})`}
                                data-testid={`crm-open-evaluation-${item.id}`}
                              >
                                <FileText className="w-4 h-4" />
                                <span className="text-xs">{format(parseISO(item.evaluacion_fecha), 'dd/MM/yy')}</span>
                              </Button>
                            </Link>
                          )}
                          <Link to={`/admin/crm-cirugias/${item.id}`}>
                            <Button size="sm" variant="outline" title="Ver ficha">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          {item.estado === 'activo' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(item.id, 'operado')}
                                className="text-green-600 hover:bg-green-50"
                                title="Marcar como operado"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(item.id, 'desistio')}
                                className="text-red-600 hover:bg-red-50"
                                title="Marcar como desistió"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t" data-testid="crm-pagination">
            <p className="text-sm text-slate-500" data-testid="crm-pagination-info">
              {totalInterested === 0
                ? '0 interesados'
                : `Mostrando ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalInterested)} de ${totalInterested}`}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="crm-prev-page">Anterior</Button>
              <span className="text-sm text-slate-600" data-testid="crm-page-indicator">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="crm-next-page">Siguiente</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* New Interested Modal */}
      <Dialog open={showNewModal} onOpenChange={(open) => {
        setShowNewModal(open);
        if (!open) {
          setIsNewPatient(false);
          setNewPatient({ full_name: '', phone: '', email: '' });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Nuevo paciente interesado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Toggle between existing/new patient */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => setIsNewPatient(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !isNewPatient ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Paciente existente
              </button>
              <button
                onClick={() => setIsNewPatient(true)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  isNewPatient ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Nuevo paciente
              </button>
            </div>

            {!isNewPatient ? (
              /* Existing patient selector */
              <>
                <div>
                  <Label>Seleccionar paciente</Label>
                  <PatientSearchSelect token={token} onSelect={handlePatientSelect} testId="crm-patient-search" />
                </div>
                {newInterested.paciente_id && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Teléfono</Label>
                      <Input
                        value={newInterested.telefono}
                        onChange={(e) => setNewInterested(prev => ({ ...prev, telefono: e.target.value }))}
                        placeholder="+502 XXXX-XXXX"
                      />
                    </div>
                    <div>
                      <Label>Correo</Label>
                      <Input
                        value={newInterested.correo}
                        onChange={(e) => setNewInterested(prev => ({ ...prev, correo: e.target.value }))}
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* New patient form */
              <>
                <div>
                  <Label>Nombre completo *</Label>
                  <Input
                    value={newPatient.full_name}
                    onChange={(e) => setNewPatient(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Nombre del paciente"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Teléfono *</Label>
                    <Input
                      value={newPatient.phone}
                      onChange={(e) => setNewPatient(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+502 XXXX-XXXX"
                    />
                  </div>
                  <div>
                    <Label>Correo</Label>
                    <Input
                      value={newPatient.email}
                      onChange={(e) => setNewPatient(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de cirugía *</Label>
                <Select 
                  value={newInterested.tipo_cirugia_id} 
                  onValueChange={(v) => {
                    if (v === '__new__') {
                      setShowNewModal(false);
                      setShowTypesModal(true);
                    } else {
                      setNewInterested(prev => ({ ...prev, tipo_cirugia_id: v }));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {surgeryTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                    <SelectItem value="__new__" className="text-blue-600 font-medium border-t mt-1 pt-2">
                      ➕ Agregar nuevo tipo...
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nivel de interés</Label>
                <Select 
                  value={newInterested.nivel_interes} 
                  onValueChange={(v) => setNewInterested(prev => ({ ...prev, nivel_interes: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alto">Alto - Decidido</SelectItem>
                    <SelectItem value="medio">Medio - Con objeciones</SelectItem>
                    <SelectItem value="bajo">Bajo - Solo preguntó</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observaciones</Label>
              <Textarea
                value={newInterested.observaciones_generales}
                onChange={(e) => setNewInterested(prev => ({ ...prev, observaciones_generales: e.target.value }))}
                placeholder="Notas adicionales..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateInterested} className="bg-[#c2410c] hover:bg-[#a13308] text-white">
              {isNewPatient ? 'Crear paciente y registrar' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">
              Registrar contacto - {selectedInterested?.nombre_completo}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Medio de contacto</Label>
                <Select value={newContact.medio} onValueChange={(v) => setNewContact(prev => ({ ...prev, medio: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Resultado</Label>
                <Select value={newContact.resultado} onValueChange={(v) => setNewContact(prev => ({ ...prev, resultado: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_RESULTS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notas del contacto (obligatorio)</Label>
              <Textarea
                value={newContact.notas}
                onChange={(e) => setNewContact(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="¿Qué dijo el paciente? ¿Objeciones? ¿Próximos pasos?"
                rows={4}
              />
            </div>
            <div>
              <Label>¿Actualizar nivel de interés?</Label>
              <Select 
                value={newContact.nivel_interes_actualizado} 
                onValueChange={(v) => setNewContact(prev => ({ ...prev, nivel_interes_actualizado: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Mantener actual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Mantener actual</SelectItem>
                  <SelectItem value="alto">Subir a Alto</SelectItem>
                  <SelectItem value="medio">Cambiar a Medio</SelectItem>
                  <SelectItem value="bajo">Bajar a Bajo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>Cancelar</Button>
            <Button onClick={handleCreateContact} className="bg-[#c2410c] hover:bg-[#a13308] text-white">
              Guardar contacto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Surgery Types Modal */}
      <Dialog open={showTypesModal} onOpenChange={setShowTypesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Gestionar tipos de cirugía</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {surgeryTypes.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No hay tipos de cirugía registrados</p>
              ) : (
                surgeryTypes.map(type => (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium text-slate-800">{type.name}</span>
                      {type.description && (
                        <p className="text-xs text-slate-500 mt-0.5">{type.description}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-300">Activo</Badge>
                  </div>
                ))
              )}
            </div>
            <div className="border-t pt-4 space-y-3">
              <Label className="text-sm font-medium">Agregar nuevo tipo</Label>
              <Input
                value={newSurgeryType.name}
                onChange={(e) => setNewSurgeryType(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre (ej: Catarata, LASIK, Pterigión)"
              />
              <Input
                value={newSurgeryType.description}
                onChange={(e) => setNewSurgeryType(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción breve (opcional)"
              />
              <Button 
                onClick={handleCreateSurgeryType} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!newSurgeryType.name}
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar tipo de cirugía
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurgeryCRMPage;
